import { Bell, ChevronDown, LogOut, Settings, User, Plus, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import { useAuth, useFamily, usePermissions } from '@/hooks'
import { ThemeToggle } from '@/components/shared/ThemeToggle'
import { cn } from '@/lib/utils'
import type { FamilyRole } from '@/types'

interface HeaderProps {
  unreadAlerts?: number
  onMenuClick?: () => void
  showMenuButton?: boolean
}

function getRoleBadgeVariant(role: FamilyRole): 'default' | 'secondary' | 'outline' {
  switch (role) {
    case 'PRIMARY_CAREGIVER':
      return 'default'
    case 'CAREGIVER':
      return 'secondary'
    default:
      return 'outline'
  }
}

function getRoleShortName(role: FamilyRole): string {
  switch (role) {
    case 'PRIMARY_CAREGIVER':
      return 'Primary'
    case 'CAREGIVER':
      return 'Caregiver'
    case 'PARENT':
      return 'Parent'
    case 'VIEWER':
      return 'Viewer'
    default:
      return role
  }
}

export function Header({ unreadAlerts = 0, onMenuClick, showMenuButton = false }: HeaderProps) {
  const navigate = useNavigate()
  const { isCaregiver } = usePermissions()
  const { user, signOut } = useAuth()
  const { families, selectedFamily, setSelectedFamilyId } = useFamily()

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const handleCreateFamily = () => {
    navigate('/onboarding/create-family')
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-4 border-b border-gray-200 bg-surface px-4 sm:px-6">
      {/* Mobile menu button */}
      {showMenuButton && (
        <Button variant="ghost" size="sm" className="lg:hidden" onClick={onMenuClick}>
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
          <span className="sr-only">Open sidebar</span>
        </Button>
      )}

      {/* Family selector - always show if user has families */}
      {families.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <span className="max-w-[150px] truncate">{selectedFamily?.name ?? 'Select family'}</span>
            {selectedFamily?.role && (
              <Badge variant={getRoleBadgeVariant(selectedFamily.role)} className="text-xs">
                {getRoleShortName(selectedFamily.role)}
              </Badge>
            )}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Switch Family</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {families.map((family) => (
              <DropdownMenuItem
                key={family.id}
                onClick={() => setSelectedFamilyId(family.id)}
                className={cn(
                  'flex items-center justify-between',
                  selectedFamily?.id === family.id && 'bg-primary-50'
                )}
              >
                <div className="flex items-center gap-2">
                  {selectedFamily?.id === family.id && (
                    <Check className="h-4 w-4 text-primary-600" />
                  )}
                  <span className={cn(
                    selectedFamily?.id === family.id ? 'text-primary-700 font-medium' : ''
                  )}>
                    {family.name}
                  </span>
                </div>
                {family.role && (
                  <Badge variant={getRoleBadgeVariant(family.role)} className="text-xs">
                    {getRoleShortName(family.role)}
                  </Badge>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleCreateFamily} className="text-primary-600">
              <Plus className="mr-2 h-4 w-4" />
              Create New Family
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Theme toggle */}
      <ThemeToggle />

      {/* Alerts button */}
      {isCaregiver && (
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => navigate('/alerts')}
      >
        <Bell className="h-5 w-5 text-gray-500" />
        {unreadAlerts > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-error-500 text-xs font-medium text-white">
            {unreadAlerts > 9 ? '9+' : unreadAlerts}
          </span>
        )}
        <span className="sr-only">View alerts</span>
      </Button>
      )}

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
          <Avatar size="sm">
            <AvatarImage src={undefined} alt={user?.full_name} />
            <AvatarFallback>{getInitials(user?.full_name ?? 'U')}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="font-medium">{user?.full_name}</span>
              <span className="text-xs font-normal text-gray-500">{user?.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <User className="mr-2 h-4 w-4" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-error-600">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
